import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiInfo ,FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import useLocalStorage from '../hooks/useLocalStorage';
import ErrorBoundary from './ErrorBoundary';
import LoadingSpinner from './LoadingSpinner';
import ChatbotPreview from './ChatbotPreview';
import { themes, themesList, getThemeById } from '../themes';
import ThemeIcon from './ThemeIcon';
import SlideshowSelector from './SlideshowSelector';
import { saveChatbotSettings, getChatbotSettings, uploadIcon } from '../api';
import './chatbot-animations.css';
import '../styles/scrollbar.css';
import { getAvailableModels } from '../api';
import MobileLayout, { MOBILE_BREAKPOINT } from './MobileLayout';

// Error Boundary Component
const ThemeErrorBoundary = ({ children }) => (
    <ErrorBoundary
        fallback={
            <div className="p-4 bg-red-50 text-red-600 rounded-lg">
                <h3 className="font-bold">Something went wrong loading the theme</h3>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Reload Page
                </button>
            </div>
        }
    >
        {children}
    </ErrorBoundary>
);

// Loading Component
const ThemeLoading = () => (
    <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" color="blue" />
    </div>
);

const ChatbotDemo = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  // All state hooks at the top
  const [activeTheme, setActiveTheme] = useLocalStorage('preferredTheme', 'professional');
  const [userId, setUserId] = useState('');
  const [chatbotId, setChatbotId] = useState('');
  const [iconUrl, setIconUrl] = useLocalStorage('iconUrl', '');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [inputText, setInputText] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [historyLength, setHistoryLength] = useState(10);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState(null);
  const [modelInfo, setModelInfo] = useState(null);
  const [loadingModelInfo, setLoadingModelInfo] = useState(false);
  const [showModelPanel, setShowModelPanel] = useState(false);
  const [activeResponseStyle, setActiveResponseStyle] = useLocalStorage('activeResponseStyle', 'professional');
  const [chatbotType, setChatbotType] = useLocalStorage('chatbotType', 'rag');
  const [allModels, setAllModels] = useState([]);
  const [selectedModelName, setSelectedModelName] = useState('');

  // All callback hooks
  const handleModelInfoClick = useCallback(async () => {
    setShowModelPanel(true);
    setLoadingModelInfo(true);
    try {
      const modelsData = await getAvailableModels();
      setAvailableModels(modelsData.map(model => ({
        id: model.model_id,
        name: model.visible_name,
        description: model.description || 'No description available',
        costPerMessage: model.cost_per_message
      })));
    } catch (error) {
      console.error('Error fetching models:', error);
      setToastMessage('Failed to load available models');
      setShowToast(true);
    } finally {
      setLoadingModelInfo(false);
    }
  }, []);

  const closeModelPanel = useCallback(() => {
    setShowModelPanel(false);
    setModelInfo(null);
  }, []);

  const handleThemeChange = useCallback((theme) => {
    setActiveTheme(theme.config.id);
    setToastMessage(`Theme changed to ${theme.config.name}`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, [setActiveTheme]);

  // All effect hooks
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    // console.log('Current allModels state:', allModels);
  }, [allModels]);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Utility function to format cost

  if (isMobile) {
    return (
      <MobileLayout 
        pageTitle="Chatbot Settings"
        restrictionTitle="Chatbot Customization Needs More Space"
        restrictionMessage="Customizing your AI chatbot requires detailed configuration, theme previews, and comprehensive settings. A larger screen ensures you can properly customize all aspects of your chatbot."
        features={[
          "Preview different chatbot themes",
          "Configure response styles",
          "Customize appearance settings",
          "Test chatbot interactions",
          "Access advanced customization options"
        ]}
        proTip="For the best customization experience, please use an iPad, tablet, laptop, or desktop computer to configure your chatbot settings."
      />
    );
  }

  // Utility function to format cost
  const formatCost = (cost) => {
    return cost.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 7,
        maximumFractionDigits: 8,
    });
  };

  // Model info panel related functions

  const chatbotTypes = [
    {
        id: 'rag',
        name: 'Document-Based AI',
        description: 'Answers questions using the documents you provide.',
        icon: '📄',
        disabled: false,
    },
    {
        id: 'lead',
        name: 'Lead Generation',
        description: 'Specialized chatbot for lead generation and qualification.',
        icon: '🎯',
        disabled: false,
    },
    {
        id: 'solar',
        name: 'Solar Panel Flow',
        description: 'Specialized chatbot for solar panel inquiries and solutions.',
        icon: '☀️',
        disabled: false,
    },
    {
        id: 'real-estate',
        name: 'Real Estate',
        description: 'Specialized chatbot for real estate inquiries and property information.',
        icon: '🏠',
        disabled: false,
    },
    {
        id: 'custom',
        name: 'Fully Custom',
        description: 'A chatbot that follows custom-defined flows and scripts.',
        icon: '✍️',
        disabled: true,
    },
  ];

  // Response Styles Data (Task 2)
  const responseStyles = [
    {
        id: 'professional',
        name: 'Professional',
        description: 'Formal and business-like responses',
        preview: 'I understand your request and will assist you accordingly.',
        icon: '\ud83d\udc54'
    },
    {
        id: 'friendly',
        name: 'Friendly',
        description: 'Casual and warm responses',
        preview: 'Hey there! I\'d love to help you with that!',
        icon: '\ud83d\ude0a'
    },
    {
        id: 'emoji',
        name: 'Emoji Style',
        description: 'Responses with relevant emojis',
        preview: 'Hi! \ud83d\udc4b I\'m here to help you! \u2728',
        icon: '\ud83c\udfaf'
    },
    {
        id: 'sales',
        name: 'Sales Focused',
        description: 'Persuasive and engaging sales responses',
        preview: 'This solution would be perfect for your needs!',
        icon: '\ud83d\udcbc'
    },
    {
        id: 'humorous',
        name: 'Humorous',
        description: 'Light and funny responses',
        preview: 'Time to solve this with a smile! \ud83d\ude04',
        icon: '\ud83d\ude04'
    },
    {
        id: 'technical',
        name: 'Technical',
        description: 'Detailed technical responses',
        preview: 'According to the technical specifications...',
        icon: '\u2699\ufe0f'
    }
  ];

    // Calculate cost per message (100 input tokens, 200 output tokens)
    const calculateCostPerMessage = (inputCost, outputCost) => {
        const inputTokens = 100; // Average input tokens per message
        const outputTokens = 200; // Average output tokens per message
        const cost = ((inputTokens / 1000) * inputCost) + ((outputTokens / 1000) * outputCost);
        return cost.toFixed(6);
    };

    // Memoized theme switching function

  // Error handling for settings fetch
  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);
      setError(null);
      const response = await getChatbotSettings();
      
      if (response.user_data?.allModels) {
        setAllModels(response.user_data.allModels);
        const selectedModelData = response.user_data.allModels.find(m => m.selected);
        if (selectedModelData) {
          setSelectedModel(selectedModelData.model_id);
          setSelectedModelName(selectedModelData.visible_name);
        }
      }
      
      // Set other settings
      setHistoryLength(response.user_data?.selectedChatbotLength || 10);
      setChatbotType(response.user_data?.selectedType || 'rag');
      
      // Extract user_id and chatbot_id from response
      if (response.user_data?.user_id) {
        setUserId(response.user_data.user_id);
      }
      if (response.user_data?.chatbot_id) {
        setChatbotId(response.user_data.chatbot_id);
        // Also store in localStorage for ChatbotPreview to use
        localStorage.setItem('chatbot_id', response.user_data.chatbot_id);
      }
      
    } catch (error) {
      setError(error.message);
    } finally {
      setLoadingSettings(false);
    }
  };

  // Error display
  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg">
        <h2 className="text-xl font-bold text-red-700 mb-2">Error Loading Chatbot</h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchSettings}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Loading state
  if (loadingSettings) {
    return <ThemeLoading />;
  }

    // Navigation functions
    const handleNavigation = (direction) => {
        if (direction === 'next') {
            setCurrentIndex((prev) => (prev + 1) % themesList.length);
        } else {
            setCurrentIndex((prev) => (prev - 1 + themesList.length) % themesList.length);
        }
    };

    // Get current themes to display
    const getCurrentThemes = () => {
        const firstTheme = themesList[currentIndex];
        const secondTheme = themesList[(currentIndex + 1) % themesList.length];
        return [firstTheme, secondTheme];
    };

    // Get current theme
    const getCurrentTheme = () => getThemeById(activeTheme);

    // Function to determine which case to show for each theme
    const getThemeCase = (themeId) => {
        const caseMap = {
            'professional': 1, // Case 1: Show options
            'dark': 2,         // Case 2: User types option
            'emoji': 3,        // Case 3: User asks new question
            'glassmorphism': 1, // Case 1: Show options
            'hologram': 2,     // Case 2: User types option
            'synthwave': 3,    // Case 3: User asks new question
            'nature': 1,       // Case 1: Show options
            'cartoonish': 2,   // Case 2: User types option
            'atlantic': 3      // Case 3: User asks new question
        };
        return caseMap[themeId] || 1; // Default to Case 1
    };

    // Function to render different chat cases
    const renderChatCase = (theme, caseNumber) => {
        const { config, styles, components } = theme;
        
        const renderTypingIndicator = () => (
            <div className="flex items-start">
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background} mr-2`}>
                    <i className={`fas fa-${config.icon} text-xs ${styles.avatar.text}`}></i>
                </div>
                <div className={`${styles.typingIndicator.container} rounded-lg p-3 shadow-sm inline-flex items-center`}>
                    {components?.TypingIndicator ? (
                        <components.TypingIndicator />
                    ) : (
                        <div className="flex space-x-1">
                            <div className={`w-2 h-2 rounded-full animate-bounce ${styles.typingIndicator.dots}`}></div>
                            <div className={`w-2 h-2 rounded-full animate-bounce ${styles.typingIndicator.dots}`} style={{ animationDelay: '0.2s' }}></div>
                            <div className={`w-2 h-2 rounded-full animate-bounce ${styles.typingIndicator.dots}`} style={{ animationDelay: '0.4s' }}></div>
                        </div>
                    )}
                </div>
            </div>
        );
        
        switch (caseNumber) {
            case 1: // Case 1: User Clicks an Option - Show options
                return (
                    <>
                        {/* Message from Bot */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>How can I help you today?</p>
                            </div>
                        </div>

                        {/* Options for Case 1 */}
                        <div className="flex flex-wrap gap-2 mt-2 mb-4 pl-12 pr-4">
                            <button className="bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md">
                                Tell me about your services
                            </button>
                            <button className="bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md">
                                Contact support
                            </button>
                            <button className="bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md">
                                Pricing information
                            </button>
                        </div>

                        {/* Typing Indicator */}
                        {renderTypingIndicator()}
                    </>
                );

            case 2: // Case 2: User Types an Option - Show user typing and bot response
                return (
                    <>
                        {/* Message from Bot */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>What would you like to know?</p>
                            </div>
                        </div>

                        {/* Message from User - Typing an option */}
                        <div className="flex justify-end mb-4">
                            <div className={`${styles.messages.user.background} rounded-lg p-3 shadow-sm max-w-[70%] ${styles.messages.user.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>Tell me about your services</p>
                            </div>
                        </div>

                        {/* Bot response to typed option */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>Great! We offer comprehensive solutions including...</p>
                            </div>
                        </div>

                        {/* Typing Indicator */}
                        {renderTypingIndicator()}
                    </>
                );

            case 3: // Case 3: User Asks New Question - Bot answers then repeats options
                return (
                    <>
                        {/* Message from Bot - Welcome */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>{config.welcomeMessage}</p>
                            </div>
                        </div>

                        {/* Message from User - Asking a question */}
                        <div className="flex justify-end mb-4">
                            <div className={`${styles.messages.user.background} rounded-lg p-3 shadow-sm max-w-[70%] ${styles.messages.user.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>What are your business hours?</p>
                            </div>
                        </div>

                        {/* Message from Bot - Answering the question */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>Our business hours are Monday to Friday, 9 AM to 6 PM EST.</p>
                            </div>
                        </div>

                        {/* Bot repeating the last options question */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>Now, how can I help you today?</p>
                            </div>
                        </div>

                        {/* Options after the repeated question */}
                        <div className="flex flex-wrap gap-2 mt-2 mb-4 pl-12 pr-4">
                            <button className="bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md">
                                Tell me about your services
                            </button>
                            <button className="bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md">
                                Contact support
                            </button>
                            <button className="bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md">
                                Pricing information
                            </button>
                        </div>

                        {/* Typing Indicator */}
                        {renderTypingIndicator()}
                    </>
                );

            default:
                return null;
        }
    };

    // Handle saving customization settings
    const handleCustomizationSave = async () => {
        // console.log('Saving settings with:', { selectedModel, activeResponseStyle, historyLength, chatbotType }); // Added for debugging
        try {
            await saveChatbotSettings({
                model: selectedModel,
                response_style: activeResponseStyle,
                history_length: historyLength,
                chatbot_type: chatbotType,
            });
            setToastMessage('Settings saved successfully!');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
        } catch (err) {
            setError(err.message);
            setToastMessage('Failed to save settings');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
        }
    };

    // Get embed code for a theme
    const getUpdatedEmbedCode = (theme) => {
        return `
<script>
window.chatConfig = {
    theme: "${theme.config.id}",
    welcomeMessage: "${theme.config.welcomeMessage}",
    userId: "${userId || 'user_default'}",
    chatbotId: "${chatbotId || 'bot_default'}",
    iconUrl: ${iconUrl ? `"${iconUrl}"` : 'null'},
    name: "${theme.config.name}",
    description: "${theme.config.description}"
};
</script>
<script src="https://linkforce-ai.netlify.app/chatbot-loader.js"></script>`;
    };



    // Copy embed code to clipboard
    const copyCode = (theme) => {
        const embedCode = getUpdatedEmbedCode(theme);
        navigator.clipboard.writeText(embedCode).then(() => {
            setToastMessage(`Copied ${theme.config.name} theme code to clipboard!`);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
        });
    };

  // Handle icon upload
    const handleIconUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check file size (1MB = 1024 * 1024 bytes)
            const maxSize = 1024 * 1024; // 1MB
            if (file.size > maxSize) {
                setToastMessage('File size must be less than 1MB');
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
                return;
            }

            // Create an image element to check dimensions
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = async () => {
                URL.revokeObjectURL(objectUrl); // Clean up

                const isValid64 = img.width === 64 && img.height === 64;
                const isValid128 = img.width === 128 && img.height === 128;
                const isValid256 = img.width === 256 && img.height === 256;
                const isValid72 = img.width === 72 && img.height === 72;
                const isvalid48 = img.width === 48 && img.height === 48;
                const isvalid96 = img.width === 96 && img.height === 96;

                if (!isValid64 && !isValid128 && !isValid256 && !isValid72 && !isvalid48 && !isvalid96) {
                    setToastMessage('Please upload an image that is either 64x64, 128x128, 256x256, 72x72, 48x48, or 96x96 pixels. Max file size: 1MB. Supports PNG, JPG, SVG.');
                    setShowToast(true);
                    setTimeout(() => setShowToast(false), 3000);
                    return;
                }

                try {
                    // Upload to server
                    const response = await uploadIcon(file, chatbotId);
                    // console.log('Upload response:', response);

                    // If upload successful, update local display
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const base64String = reader.result;
                        setIconUrl(base64String);
                        localStorage.setItem('iconUrl', base64String);
                        setToastMessage(`Company icon (${img.width}x${img.height}) updated successfully!`);
                        setShowToast(true);
                        setTimeout(() => setShowToast(false), 3000);
                    };
                    reader.readAsDataURL(file);
                } catch (error) {
                    setToastMessage(error.message || 'Failed to upload icon');
                    setShowToast(true);
                    setTimeout(() => setShowToast(false), 3000);
                }
            };

            img.src = objectUrl;
        }
    };
 
     // Handle suggested prompt click
     const handlePromptClick = (promptText) => {
        setInputText(promptText);
        // Optionally, automatically send the message after a short delay
        // or wait for the user to press send.
        // For now, let's assume the user will press send or Enter.
        // If you want to auto-send:
        // setTimeout(() => handleSendMessage(), 100);
    };

    // Remove custom icon
    const removeCustomIcon = () => {
        setIconUrl('');
        localStorage.removeItem('iconUrl');
        setToastMessage('Company icon removed');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Handle custom option selection
    const handleCustomOptionSelect = (optionType, optionId) => {
        if (optionId === 'custom') {
            setToastMessage(`${optionType} customization is in development and will be available soon! 🚀`);
            setShowToast(true);
            setTimeout(() => setShowToast(false), 4000);
            return true; // Indicates this was a custom option
        }
        return false; // Not a custom option
    };

    // Handle response style selection
    const handleResponseStyleSelect = (styleId) => {
        if (!handleCustomOptionSelect('Response Style', styleId)) {
            setActiveResponseStyle(styleId);
        }
    };

    // Handle chatbot type selection
    const handleChatbotTypeSelect = (typeId) => {
        if (!handleCustomOptionSelect('Chatbot Type', typeId)) {
            setChatbotType(typeId);
        }
    };

    if (loadingSettings || loadingModels) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LoadingSpinner size="lg" color="blue" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex">
            {/* Fixed Sidebar - Enhanced Design */}
            <div className="w-96 bg-gradient-to-br from-white via-gray-50 to-blue-50/30 shadow-[0_4px_30px_-10px_rgba(0,0,0,0.1)] h-screen sticky top-0 border-r border-gray-100/50 backdrop-blur-sm">
                <div className="p-8 h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {/* Header with Animated Accent */}
                    <div className="mb-10 relative group">
                        <div className="absolute -left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full transform transition-all duration-300 group-hover:scale-y-110"></div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                            Customize Chatbot
                        </h2>
                        <p className="text-sm text-gray-500">Personalize your chatbot's behavior and appearance</p>
                    </div>

                    {/* Company Icon Upload Section */}
                    <div className="mb-8 bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.15)] transition-all duration-300 transform hover:-translate-y-1">
                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <i className="fas fa-image mr-2 text-blue-500"></i>
                            Company Icon
                        </label>
                        <div className="space-y-3">
                            {/* Preview */}
                            {iconUrl && (
                                <div className="flex items-center justify-center mb-3">
                                    <div className="w-16 h-16 rounded-full border-2 border-blue-100 flex items-center justify-center overflow-hidden bg-white shadow-inner group relative">
                                        <img
                                            src={iconUrl}
                                            alt="Company Icon"
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </div>
                                </div>
                            )}

                            {/* Upload Button */}
                            <div className="flex flex-col gap-2">
                                <label className="w-full py-3 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-xl text-center cursor-pointer hover:border-blue-300 hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 group relative overflow-hidden">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleIconUpload}
                                        className="hidden"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <span className="text-sm font-medium text-blue-700 flex items-center justify-center gap-2 relative z-10">
                                        <i className="fas fa-cloud-upload-alt group-hover:scale-110 transition-transform duration-300"></i>
                                        {iconUrl ? 'Change Icon' : 'Upload Icon'}
                                    </span>
                                </label>

                                {iconUrl && (
                                    <button
                                        onClick={removeCustomIcon}
                                        className="w-full py-3 px-4 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm hover:bg-red-100 transition-all duration-300 flex items-center justify-center gap-2 group relative overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-red-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        <i className="fas fa-trash-alt group-hover:scale-110 transition-transform duration-300"></i>
                                        <span className="font-medium relative z-10">Remove Icon</span>
                                    </button>
                                )}
                            </div>

                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl mt-4">
                                <p className="text-xs text-gray-600 flex items-start">
                                    <i className="fas fa-info-circle text-blue-500 mr-2 mt-0.5"></i>
                                    <span>Upload a square image (64x64, 128x128, or 256x256 pixels). Max size: 1MB. Supports PNG, JPG, SVG.</span>
                            </p>
                        </div>
                    </div>
                        
                        {/* Visual Separator */}
                        <div className="mb-8 flex items-center gap-3">
                            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent flex-grow"></div>
                            <div className="text-xs text-gray-400 font-medium">Settings</div>
                            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent flex-grow"></div>
                    </div>

                    {/* Response Style Selection */}
                        <div className="mb-8">
                            <div className="flex items-center mb-4">
                                <i className="fas fa-comments text-blue-500 mr-2"></i>
                                <h3 className="text-sm font-semibold text-gray-700">Response Style</h3>
                            </div>
                        <SlideshowSelector
                            label="Response Style"
                            items={responseStyles}
                            selectedId={activeResponseStyle}
                            onSelect={handleResponseStyleSelect}
                        />
                    </div>

                    {/* Chatbot Type Selection */}
                    <div className="mb-8">
                            <div className="flex items-center mb-4">
                                <i className="fas fa-robot text-blue-500 mr-2"></i>
                                <h3 className="text-sm font-semibold text-gray-700">Chatbot Type</h3>
                            </div>
                        <SlideshowSelector
                            label="Chatbot Type"
                            items={chatbotTypes}
                            selectedId={chatbotType}
                            onSelect={handleChatbotTypeSelect}
                        />
                    </div>

                    {/* Model Selection */}
                    <div className="mb-8 bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.15)] transition-all duration-300 transform hover:-translate-y-1">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                                <i className="fas fa-microchip text-blue-500 mr-2"></i>
                                <h3 className="text-sm font-semibold text-gray-700">LLM Model</h3>
                            </div>
                            <button
                                type="button"
                                onClick={handleModelInfoClick}
                                className="text-blue-500 hover:text-blue-600 transition-all duration-300 hover:scale-110"
                                title="View all available models"
                            >
                                <i className="fas fa-info-circle text-lg"></i>
                            </button>
                        </div>
                        <div className="relative">
                            <select
                                value={selectedModel}
                                onChange={(e) => {
                                    const model = allModels.find(m => m.model_id === e.target.value);
                                    setSelectedModel(model.model_id);
                                    setSelectedModelName(model.visible_name);
                                }}
                                className="w-full pl-4 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white transition-all duration-300"
                            >
                                {allModels.map(model => (
                                    <option key={model.model_id} value={model.model_id}>
                                        {model.visible_name}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-blue-500">
                                <i className="fas fa-chevron-down"></i>
                            </div>
                        </div>
                    </div>

                    {/* Chat History Length */}
                        <div className="mb-10 bg-gray-50 rounded-xl p-6 border border-gray-100">
                            <div className="flex items-center mb-4">
                                <i className="fas fa-history text-blue-500 mr-2"></i>
                                <h3 className="text-sm font-semibold text-gray-700">Chat History Length: {historyLength}</h3>
                            </div>
                        <input
                            type="range"
                            min="5"
                            max="50"
                            value={historyLength}
                            onChange={(e) => setHistoryLength(parseInt(e.target.value))}
                                className="w-full accent-blue-600"
                        />
                            <div className="flex justify-between text-xs text-gray-500 mt-2">
                            <span>5 messages</span>
                            <span>50 messages</span>
                        </div>
                            <p className="text-xs text-gray-500 mt-3 bg-white p-3 rounded-lg border border-gray-100">
                                <i className="fas fa-info-circle mr-1 text-blue-500"></i>
                                Number of previous messages to include for context
                            </p>
                    </div>

                    {/* Save Button */}
                    <button
                        onClick={handleCustomizationSave}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                        <i className="fas fa-save group-hover:scale-110 transition-transform duration-300"></i>
                        <span className="relative z-10">Save Changes</span>
                    </button>
                    </div>
                </div>
            </div>

            {/* Model Information Panel */}
            <div
                className={`fixed top-0 right-0 h-full bg-gradient-to-br from-white to-gray-50 shadow-2xl z-50 transition-all duration-300 ease-in-out transform ${showModelPanel ? 'translate-x-0' : 'translate-x-full'
                    } w-[75vw]`}
                style={{
                    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.08)',
                    maxWidth: '75%',
                    minWidth: '320px'
                }}
            >
                <div className="h-full flex flex-col overflow-hidden">
                    {/* Panel Header with close button */}
                    <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-indigo-600 to-purple-600">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-white">Model Information</h2>
                            <button
                                onClick={closeModelPanel}
                                className="text-white hover:bg-white/10 p-1.5 rounded-full transition-colors duration-200"
                                aria-label="Close panel"
                            >
                                <FiX className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Panel Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                                <p className="text-xs text-gray-500 italic">
                                    * These are approximate costs. Actual costs may vary based on message length and complexity.
                                </p>
                            </div>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approx. Cost/Message</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {availableModels.map((model) => (
                                    <tr 
                                        key={model.name} 
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => {
                                            setSelectedModel(model.id);
                                            closeModelPanel();
                                        }}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <span className="text-black font-semibold">{model.name.charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{model.name}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900 max-w-xs line-clamp-2">
                                                {model.description}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {formatCost(model.costPerMessage)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay */}
            <div
                className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${showModelPanel ? 'opacity-50' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={closeModelPanel}
                aria-hidden="true"
            />

            {/* Main Content */}
            <div className="flex-1">
                {/* Background Elements */}
                <div className="fixed inset-0 w-full h-full overflow-hidden pointer-events-none z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-indigo-50/50 opacity-50"></div>
                </div>

                {/* Main Content Container */}
                <div className="container max-w-[90%] mx-auto px-4 md:px-6 py-12 relative z-10">
                    <div className="flex flex-col items-center gap-6">
                        <div className="flex items-center justify-center gap-8 w-full">
                            {/* Left Arrow - Enhanced */}
                            <button
                                onClick={() => handleNavigation('prev')}
                                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-blue-600 hover:border-blue-400 transition-colors duration-150 shadow-sm focus:outline-none"
                                aria-label="Previous themes"
                            >
                                <i className="fas fa-chevron-left text-base"></i>
                            </button>

                            {/* Chatbot Cards */}
                            <div className="flex gap-10 justify-center">
                                {getCurrentThemes().map(theme => {
                                    const { config, styles, components } = theme;
                                    const BackgroundEffects = components?.BackgroundEffects;

                                    return (
                                        <div
                                            key={config.id}
                                            className={`theme-card relative w-[360px] overflow-hidden border ${styles.container.backgroundColor} border-gray-100 rounded-xl bg-white transition-all duration-400 shadow-lg hover:shadow-xl hover:-translate-y-2 ${activeTheme === config.id ? `border-2 border-${config.color}-500 scale-102` : ''
                                                }`}
                                        >
                                            {BackgroundEffects && <BackgroundEffects />}
                                            {/* Theme Header */}
                                            <div className={`p-4 rounded-t-xl relative z-10 ${styles.header.background}`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <ThemeIcon iconUrl={iconUrl} config={config} styles={styles} />
                                                        <div>
                                                            <h3 className={`text-lg font-semibold ${styles.header.textColor}`}>{config.name}</h3>
                                                            <p className={`${styles.header.textColor} opacity-75 text-sm`}>{config.description}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex space-x-1">
                                                        <div className={`w-3 h-3 rounded-full bg-${config.color}-300`}></div>
                                                        <div className={`w-3 h-3 rounded-full bg-${config.color}-400`}></div>
                                                        <div className={`w-3 h-3 rounded-full bg-${config.color}-500`}></div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Chat Preview Container */}
                                            <div 
                                                className={`${styles.container.backgroundColor} p-4 relative z-10 overflow-hidden scrollbar-thin`} 
                                                style={{ 
                                                    minHeight: '250px', 
                                                    maxHeight: '300px', 
                                                    fontFamily: config.typography,
                                                    // Special handling for synthwave and hologram themes
                                                    ...(config.id === 'synthwave' || config.id === 'hologram' ? {
                                                        overflowY: 'auto',
                                                        overflowX: 'hidden'
                                                    } : {
                                                        overflow: 'auto'
                                                    })
                                                }}
                                            >
                                                {/* Background Effects Container */}
                                                <div className="absolute inset-0 overflow-hidden">
                                                    {BackgroundEffects && <BackgroundEffects />}
                                                </div>
                                                
                                                {/* Chat Content Container */}
                                                <div className="relative h-full overflow-y-auto scrollbar-thin" style={{
                                                    zIndex: 1,
                                                    // Ensure content is properly contained
                                                    maxHeight: '100%'
                                                }}>
                                                    {renderChatCase(theme, getThemeCase(config.id))}
                                                </div>
                                            </div>

                                            {/* Controls and Features */}
                                            <div className="bg-white p-4 border-x border-b border-gray-200 rounded-b-xl min-h-[220px]">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className={`text-xs font-medium text-${config.color}-500 flex items-center`}>
                                                        <i className="fas fa-info-circle mr-1"></i> Features
                                                    </div>
                                                    <div className="flex space-x-2">
                                                        <span className={`px-2 py-1 bg-${config.color}-100 text-${config.color}-600 rounded-md text-xs font-medium`}>
                                                            {config.description}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Embed Code */}
                                                <div className="mt-3 mb-4">
                                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                                                        <i className="fas fa-code mr-1"></i> Embed Code
                                                    </h4>
                                                    <div className="bg-gray-800 rounded-md p-3 relative">
                                                        <button
                                                            className={`absolute right-2 top-2 text-xs px-2 py-1 bg-${config.color}-500 text-white rounded opacity-80 hover:opacity-100 transition-opacity`}
                                                            onClick={() => copyCode(theme)}
                                                        >
                                                            Copy
                                                        </button>
                                                        <pre className="text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                                                            <code>{getUpdatedEmbedCode(theme).replace(/ data-history-length="[^"]*"/g, '')}</code>
                                                        </pre>
                                                    </div>
                                                </div>

                                                {/* Choose Theme Button */}
                                                <button
                                                    onClick={() => handleThemeChange(theme)}
                                                    className={`w-full py-3 px-4 bg-${config.color}-600 hover:bg-${config.color}-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 transform hover:-translate-y-1 hover:shadow-lg`}
                                                >
                                                    <i className="fas fa-check-circle"></i>
                                                    Choose {config.name}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Right Arrow - Enhanced */}
                            <button
                                onClick={() => handleNavigation('next')}
                                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-blue-600 hover:border-blue-400 transition-colors duration-150 shadow-sm focus:outline-none"
                                aria-label="Next themes"
                            >
                                <i className="fas fa-chevron-right text-base"></i>
                            </button>
                        </div>

                        {/* Position Indicator */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-500">
                                Viewing themes {currentIndex + 1}-{currentIndex + 2} of {themesList.length}
                            </span>
                            <div className="flex gap-1">
                                {Array.from({ length: Math.ceil(themesList.length / 2) }).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`w-2 h-2 rounded-full ${Math.floor(currentIndex / 2) === idx
                                                ? 'bg-indigo-600'
                                                : 'bg-gray-300'
                                            }`}
                                    ></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            <div 
                className={`fixed top-5 right-5 z-50 transition-all duration-300 transform ${
                    showToast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
                }`}
            >
                <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden w-80">
                    <div className="flex items-start p-4">
                        <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                        <div className="ml-3 w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                                {toastMessage}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                                Just now
                            </p>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex">
                            <button 
                                onClick={() => setShowToast(false)}
                                className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-2">
                        <div className="text-xs text-gray-500">
                            Click outside to dismiss
                        </div>
                    </div>
                </div>
            </div>

            {/* Add ChatbotPreview component */}
            <ChatbotPreview
                theme={getCurrentTheme()}
                iconUrl={iconUrl}
                isOpen={isPreviewOpen}
                setIsOpen={setIsPreviewOpen}
                userId={userId}
                chatbotId={chatbotId}
            />
        </div>
    );
};
export default ChatbotDemo; 